import { absoluteTimeoutMs, idleTimeoutMs, type SessionExpiryReason } from '../config/session-policy'
import { isNativeApp } from './platform'

const STORAGE_KEY = 'ascend-session-tracker'
const EXPIRED_FLAG_KEY = 'ascend-session-expired'

type SessionTracker = {
  signedInAt: number
  lastActivityAt: number
}

function readTracker(): SessionTracker | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as SessionTracker
    if (typeof parsed.signedInAt !== 'number' || typeof parsed.lastActivityAt !== 'number') {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function writeTracker(tracker: SessionTracker): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tracker))
}

export function hasSessionTracker(): boolean {
  return readTracker() != null
}

/** Call after a successful Cognito sign-in / session restore. */
export function recordSessionStart(): void {
  const now = Date.now()
  writeTracker({ signedInAt: now, lastActivityAt: now })
}

/** Bump last-activity timestamp (throttled by caller). */
export function touchSessionActivity(): void {
  const existing = readTracker()
  const now = Date.now()
  if (!existing) {
    writeTracker({ signedInAt: now, lastActivityAt: now })
    return
  }
  writeTracker({ ...existing, lastActivityAt: now })
}

export function clearSessionTracker(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(STORAGE_KEY)
}

export function markSessionExpired(reason: SessionExpiryReason): void {
  if (typeof window === 'undefined') return
  sessionStorage.setItem(EXPIRED_FLAG_KEY, reason)
}

export function consumeSessionExpiredReason(): SessionExpiryReason | null {
  if (typeof window === 'undefined') return null
  const value = sessionStorage.getItem(EXPIRED_FLAG_KEY)
  sessionStorage.removeItem(EXPIRED_FLAG_KEY)
  if (value === 'idle' || value === 'absolute') return value
  return null
}

export function checkSessionExpiry(now = Date.now()): SessionExpiryReason | null {
  const tracker = readTracker()
  if (!tracker) return null
  if (now - tracker.signedInAt >= absoluteTimeoutMs()) return 'absolute'
  if (now - tracker.lastActivityAt >= idleTimeoutMs(isNativeApp())) return 'idle'
  return null
}
